/**
 * RequirementsBlock - Displays mandatory requirements (SKA-krav) for tender assessment.
 *
 * Design decisions (per UX feedback):
 * - Title shows actual requirement text from document/source quote, not summarized title
 * - Type category badges intentionally hidden to reduce visual clutter
 * - requirementType data is preserved for potential filtering/export use
 */

import { useCallback, useEffect, useMemo, useRef, useState, type HTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ClipboardCheck,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Trash2,
  Plus,
  GripVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { RequirementsBlock as RequirementsBlockType } from "@/canvas/types/blocks";
import type { Requirement } from "@/canvas/types/content";
import { cn } from "@/lib/utils";
import { useCanvasStore } from "../../store";
import {
  AnalysisListAddButton,
  AnalysisListCard,
  AnalysisListCardDragHandle,
  AnalysisListEditableHeaderTitle,
  AnalysisListEditableTitle,
  AnalysisListHeader,
  AnalysisListMetric,
  AnalysisListStatusChip,
  AnalysisListStatusSelect,
  DocumentReferenceChips,
  replaceAnalysisListItems,
} from "./AnalysisList";
import { generateItemId } from "../../utils/id";

interface RequirementsBlockProps {
  readOnly?: boolean;
  block: RequirementsBlockType;
}

const statusConfig = {
  met: {
    icon: CheckCircle2,
    iconClass: "text-success",
    labelClass: "text-success/85",
    badgeClass: "border-success/18 bg-success-muted/20",
  },
  unmet: {
    icon: XCircle,
    iconClass: "text-danger",
    labelClass: "text-danger/85",
    badgeClass: "border-danger/18 bg-danger-muted/18",
  },
  needs_review: {
    icon: HelpCircle,
    iconClass: "text-warning",
    labelClass: "text-warning/90",
    badgeClass: "border-warning/18 bg-warning-muted/14",
  },
} as const;

interface RequirementCardProps {
  requirement: Requirement;
  statusLabel: string;
  readOnly: boolean;
  onUpdateRequirement: (updatedRequirement: Requirement) => void;
  onStatusChange: (status: Requirement["status"]) => void;
  onDelete: () => void;
  dragHandleProps?: HTMLAttributes<HTMLDivElement>;
}

function RequirementCard({
  requirement,
  statusLabel,
  readOnly,
  onUpdateRequirement,
  onStatusChange,
  onDelete,
  dragHandleProps,
}: RequirementCardProps) {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [localTitle, setLocalTitle] = useState(requirement.title);
  const [localDescription, setLocalDescription] = useState(requirement.description || "");

  const config = statusConfig[requirement.status] ?? statusConfig.needs_review;
  const StatusIcon = config.icon;

  useEffect(() => {
    if (isOpen && cardRef.current) {
      const timeoutId = setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isEditingTitle) {
      setLocalTitle(requirement.title);
    }
  }, [requirement.title, isEditingTitle]);

  useEffect(() => {
    if (!isEditingDescription) {
      setLocalDescription(requirement.description || "");
    }
  }, [requirement.description, isEditingDescription]);

  const displayTitle = requirement.title || requirement.sourceQuote || statusLabel;
  const statusDisplayLabel =
    {
      met: t("canvas.blocks.requirements.statusShort.met"),
      unmet: t("canvas.blocks.requirements.statusShort.unmet"),
      needs_review: t("canvas.blocks.requirements.statusShort.needs_review"),
    }[requirement.status] ?? statusLabel;

  const hasDetails = Boolean(
    requirement.sourceQuote ||
    requirement.description ||
    requirement.afCode ||
    (requirement.documentRefs?.length ?? 0) > 0 ||
    !readOnly,
  );

  const handleStartEditingTitle = useCallback(() => {
    setLocalTitle(requirement.title || displayTitle);
    setIsEditingTitle(true);
  }, [displayTitle, requirement.title]);

  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    const trimmed = localTitle.trim();
    const fallbackTitle = requirement.sourceQuote || statusLabel;

    if (!trimmed) {
      setLocalTitle(requirement.title || displayTitle);
      return;
    }

    const newTitle = !requirement.title && trimmed === fallbackTitle ? undefined : trimmed;

    if (newTitle !== requirement.title) {
      onUpdateRequirement({ ...requirement, title: newTitle });
    }
  }, [displayTitle, localTitle, onUpdateRequirement, requirement, statusLabel]);

  const handleStartEditingDescription = useCallback(() => {
    setLocalDescription(requirement.description || "");
    setIsEditingDescription(true);
  }, [requirement.description]);

  const handleDescriptionBlur = useCallback(() => {
    setIsEditingDescription(false);
    const newDescription = localDescription.trim() || undefined;
    if (newDescription !== requirement.description) {
      onUpdateRequirement({ ...requirement, description: newDescription });
    }
  }, [localDescription, onUpdateRequirement, requirement]);

  const trailing = readOnly ? (
    <AnalysisListStatusChip
      label={statusDisplayLabel}
      className={cn(config.badgeClass, config.labelClass)}
    />
  ) : (
    <AnalysisListStatusSelect
      value={requirement.status}
      displayLabel={statusDisplayLabel}
      onValueChange={(value) => onStatusChange(value as Requirement["status"])}
      options={[
        { value: "met", label: t("canvas.blocks.requirements.status.met") },
        { value: "unmet", label: t("canvas.blocks.requirements.status.unmet") },
        {
          value: "needs_review",
          label: t("canvas.blocks.requirements.status.needs_review"),
        },
      ]}
      className={cn(config.badgeClass, config.labelClass)}
    />
  );

  return (
    <AnalysisListCard
      cardRef={cardRef}
      open={isOpen}
      onOpenChange={setIsOpen}
      expandable={hasDetails}
      dragHandle={
        !readOnly && dragHandleProps ? (
          <AnalysisListCardDragHandle {...dragHandleProps}>
            <GripVertical className="h-4 w-4" />
          </AnalysisListCardDragHandle>
        ) : undefined
      }
      leading={
        <div className={cn("shrink-0", config.iconClass)}>
          <StatusIcon className="h-4 w-4" />
        </div>
      }
      title={
        <div className="min-w-0">
          <AnalysisListEditableTitle
            value={displayTitle}
            draftValue={localTitle}
            isEditing={isEditingTitle}
            isOpen={isOpen}
            readOnly={readOnly}
            onChange={setLocalTitle}
            onStartEditing={handleStartEditingTitle}
            onCommit={handleTitleBlur}
            onCancel={() => {
              setLocalTitle(requirement.title || displayTitle);
              setIsEditingTitle(false);
            }}
            displayClassName="truncate"
          />
        </div>
      }
      trailing={trailing}
      body={
        <>
          {requirement.afCode && (
            <div className="flex items-baseline gap-2">
              <span className="text-ui-eyebrow text-muted-foreground">
                {t("canvas.blocks.requirements.afCode")}:
              </span>
              <span className="text-ui-body-sm font-mono font-medium text-foreground">
                {requirement.afCode}
              </span>
            </div>
          )}
          {requirement.sourceQuote ? (
            <div className="rounded-md bg-info/10 px-3 py-2 text-ui-body-sm italic text-foreground/80">
              "{requirement.sourceQuote}"
            </div>
          ) : null}
          {!readOnly ||
          Boolean(
            requirement.description && requirement.description !== requirement.sourceQuote,
          ) ? (
            <div className="flex flex-col gap-1">
              <p className="text-ui-eyebrow text-foreground/50">
                {t("canvas.blocks.requirements.headers.description")}
              </p>
              {isEditingDescription && !readOnly ? (
                <textarea
                  value={localDescription}
                  onChange={(event) => setLocalDescription(event.target.value)}
                  onBlur={handleDescriptionBlur}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      setLocalDescription(requirement.description || "");
                      setIsEditingDescription(false);
                    }
                    event.stopPropagation();
                  }}
                  onClick={(event) => event.stopPropagation()}
                  placeholder={t("canvas.blocks.requirements.addDescription", "Add description...")}
                  className="text-ui-body-sm text-foreground/80 leading-relaxed w-full bg-transparent border-0 rounded px-1 -mx-1 py-0.5 resize-none focus-visible:ring-0 outline-none min-h-[1.5em] block"
                  autoFocus
                />
              ) : requirement.description && requirement.description !== requirement.sourceQuote ? (
                <p
                  className={cn(
                    "text-ui-body-sm text-foreground/80 whitespace-pre-wrap leading-relaxed",
                    !readOnly &&
                      "cursor-text hover:bg-muted/30 rounded px-1 -mx-1 py-0.5 transition-colors",
                  )}
                  onClick={() => {
                    if (!readOnly) {
                      handleStartEditingDescription();
                    }
                  }}
                >
                  {requirement.description}
                </p>
              ) : !readOnly ? (
                <p
                  className="text-ui-body-sm text-foreground/50 italic cursor-text hover:bg-muted/30 rounded px-1 -mx-1 py-0.5 transition-colors"
                  onClick={() => {
                    handleStartEditingDescription();
                  }}
                >
                  {t("canvas.blocks.requirements.addDescription", "Add description...")}
                </p>
              ) : null}
            </div>
          ) : null}
          <DocumentReferenceChips refs={requirement.documentRefs} />
        </>
      }
      footer={
        !readOnly ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            {t("canvas.blocks.requirements.removeRequirement")}
          </Button>
        ) : undefined
      }
      footerClassName="justify-end"
    />
  );
}

interface SortableRequirementCardProps extends Omit<RequirementCardProps, "dragHandleProps"> {
  id: string;
}

function SortableRequirementCard({ id, ...props }: SortableRequirementCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <RequirementCard {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </div>
  );
}

export function RequirementsBlock({ block, readOnly = false }: RequirementsBlockProps) {
  const { t } = useTranslation();
  const variant = block.content.variant;
  type RequirementsVariant = NonNullable<typeof variant>;
  const variantTitleKeys: Record<RequirementsVariant, string> = {
    eligibility: "canvas.blocks.requirements.header_eligibility",
    execution: "canvas.blocks.requirements.header_execution",
  };
  const defaultHeaderTitle =
    variant && variantTitleKeys[variant]
      ? t(variantTitleKeys[variant])
      : t("canvas.blocks.requirements.header");
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [localHeader, setLocalHeader] = useState(block.title || defaultHeaderTitle);
  const updateBlockContent = useCanvasStore((state) => state.updateBlockContent);
  const updateBlock = useCanvasStore((state) => state.updateBlock);

  useEffect(() => {
    if (!isEditingHeader) {
      setLocalHeader(block.title || defaultHeaderTitle);
    }
  }, [block.title, defaultHeaderTitle, isEditingHeader]);

  const requirements = useMemo<Requirement[]>(() => {
    return (block.content.items as Requirement[] | undefined) ?? [];
  }, [block.content.items]);

  const statusLabels: Record<Requirement["status"], string> = {
    met: t("canvas.blocks.requirements.status.met"),
    unmet: t("canvas.blocks.requirements.status.unmet"),
    needs_review: t("canvas.blocks.requirements.status.needs_review"),
  };

  const headerTitle = block.title || defaultHeaderTitle;

  const handleStartEditingHeader = useCallback(() => {
    setLocalHeader(block.title || defaultHeaderTitle);
    setIsEditingHeader(true);
  }, [block.title, defaultHeaderTitle]);

  const handleHeaderBlur = useCallback(() => {
    setIsEditingHeader(false);
    const trimmedTitle = localHeader.trim();
    const newTitle = trimmedTitle && trimmedTitle !== defaultHeaderTitle ? trimmedTitle : undefined;
    if (newTitle !== block.title) {
      updateBlock(block.id, { title: newTitle });
    }
  }, [block.id, block.title, defaultHeaderTitle, localHeader, updateBlock]);

  const setRequirementItems = useCallback(
    (items: Requirement[]) => {
      updateBlockContent(
        block.id,
        replaceAnalysisListItems(block.content, items) as Parameters<typeof updateBlockContent>[1],
      );
    },
    [block.content, block.id, updateBlockContent],
  );

  const handleUpdateRequirement = useCallback(
    (updatedRequirement: Requirement) => {
      setRequirementItems(
        requirements.map((requirement) =>
          requirement.id === updatedRequirement.id ? updatedRequirement : requirement,
        ),
      );
    },
    [requirements, setRequirementItems],
  );

  const handleAddRequirement = useCallback(() => {
    const newRequirement: Requirement = {
      id: generateItemId(),
      title: t("canvas.blocks.requirements.newRequirementTitle", "New requirement"),
      description: undefined,
      requirementType: "other",
      status: "needs_review",
      sourceQuote: undefined,
      afCode: undefined,
      documentRefs: [],
      knowledgeBaseRefs: [],
    };
    setRequirementItems([...requirements, newRequirement]);
  }, [requirements, setRequirementItems, t]);

  const handleStatusChange = useCallback(
    (requirementId: string, newStatus: Requirement["status"]) => {
      setRequirementItems(
        requirements.map((requirement) =>
          requirement.id === requirementId ? { ...requirement, status: newStatus } : requirement,
        ),
      );
    },
    [requirements, setRequirementItems],
  );

  const handleDelete = useCallback(
    (requirementId: string) => {
      setRequirementItems(requirements.filter((requirement) => requirement.id !== requirementId));
    },
    [requirements, setRequirementItems],
  );

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = requirements.findIndex((requirement) => requirement.id === active.id);
      const newIndex = requirements.findIndex((requirement) => requirement.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const nextRequirements = [...requirements];
      const [movedRequirement] = nextRequirements.splice(oldIndex, 1);
      nextRequirements.splice(newIndex, 0, movedRequirement);
      setRequirementItems(nextRequirements);
    },
    [requirements, setRequirementItems],
  );

  const stats = useMemo(() => {
    const counts: Record<Requirement["status"], number> = { met: 0, unmet: 0, needs_review: 0 };
    requirements.forEach((requirement) => {
      if (requirement.status in counts) counts[requirement.status]++;
    });
    return counts;
  }, [requirements]);

  if (requirements.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-muted-foreground/20 p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted/50 mb-3">
          <ClipboardCheck className="h-6 w-6 text-muted-foreground/50" />
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          {t("canvas.blocks.requirements.empty")}
        </p>
        {!readOnly ? (
          <Button variant="outline" size="sm" onClick={handleAddRequirement}>
            <Plus className="h-4 w-4 mr-1.5" />
            {t("canvas.blocks.requirements.addRequirement", "Add requirement")}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <AnalysisListHeader
        title={
          <AnalysisListEditableHeaderTitle
            value={headerTitle}
            draftValue={localHeader}
            isEditing={isEditingHeader}
            readOnly={readOnly}
            onChange={setLocalHeader}
            onStartEditing={handleStartEditingHeader}
            onCommit={handleHeaderBlur}
            onCancel={() => {
              setLocalHeader(block.title || defaultHeaderTitle);
              setIsEditingHeader(false);
            }}
          />
        }
        metrics={Object.entries(stats).map(([status, count]) => {
          if (count === 0) return null;

          const config = statusConfig[status as Requirement["status"]];
          const Icon = config.icon;

          return (
            <AnalysisListMetric
              key={status}
              icon={<Icon className={cn("h-4 w-4", config.iconClass)} />}
              value={count}
              tooltip={
                <>
                  {count} {statusLabels[status as Requirement["status"]].toLowerCase()}
                </>
              }
            />
          );
        })}
        trailing={
          <AnalysisListMetric
            icon={<ClipboardCheck className="h-4 w-4 text-muted-foreground" />}
            value={requirements.length}
            className="text-sm text-muted-foreground"
            tooltip={
              <p>
                {requirements.length}{" "}
                {requirements.length === 1
                  ? t("canvas.blocks.requirements.requirementSingular")
                  : t("canvas.blocks.requirements.requirementPlural")}
              </p>
            }
          />
        }
      />

      {readOnly ? (
        <div className="flex flex-col gap-2">
          {requirements.map((requirement) => (
            <RequirementCard
              key={requirement.id}
              requirement={requirement}
              statusLabel={
                statusLabels[requirement.status] ??
                t("canvas.blocks.requirements.status.needs_review")
              }
              readOnly={readOnly}
              onUpdateRequirement={handleUpdateRequirement}
              onStatusChange={(status) => handleStatusChange(requirement.id, status)}
              onDelete={() => handleDelete(requirement.id)}
            />
          ))}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={requirements.map((requirement) => requirement.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-2">
              {requirements.map((requirement) => (
                <SortableRequirementCard
                  key={requirement.id}
                  id={requirement.id}
                  requirement={requirement}
                  statusLabel={
                    statusLabels[requirement.status] ??
                    t("canvas.blocks.requirements.status.needs_review")
                  }
                  readOnly={readOnly}
                  onUpdateRequirement={handleUpdateRequirement}
                  onStatusChange={(status) => handleStatusChange(requirement.id, status)}
                  onDelete={() => handleDelete(requirement.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {!readOnly ? (
        <AnalysisListAddButton onClick={handleAddRequirement}>
          <Plus className="h-4 w-4" />
          {t("canvas.blocks.requirements.addRequirement", "Add requirement")}
        </AnalysisListAddButton>
      ) : null}
    </div>
  );
}
